import { OTLayout } from "@/components/layout/ot-layout";
import { useState, useEffect, useCallback } from "react";
import { Star, ChevronDown, ChevronUp, Loader2, Save } from "lucide-react";

type StageStatus = "developing" | "achieving" | "achieved";

const STATUS_LABELS: Record<StageStatus, string> = {
  developing: "Developing",
  achieving: "Achieving",
  achieved: "Achieved",
};

const STATUS_COLORS: Record<StageStatus, string> = {
  developing: "bg-amber-50 text-amber-700 border-amber-200",
  achieving: "bg-blue-50 text-blue-700 border-blue-200",
  achieved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const RCOT_STAGES = [
  {
    key: "proficient",
    title: "Proficient",
    description: "Competent occupational therapist with consolidated clinical skills, working independently across a defined area of practice.",
    band: "Typically Band 6–7",
    competencies: [
      { key: "clinical_reasoning", label: "Advanced clinical reasoning and complex case management" },
      { key: "assessment_tools", label: "Skilled use of standardised OT assessment tools (COPM, AMPS, DASH)" },
      { key: "specialist_interventions", label: "Delivery of specialist OT interventions (joint protection, fatigue management, splinting)" },
      { key: "mdt_collaboration", label: "Effective MDT collaboration and integrated care pathway working" },
      { key: "supervision_received", label: "Engaging actively with clinical supervision and reflective practice" },
    ],
    colour: "blue",
  },
  {
    key: "expert",
    title: "Expert",
    description: "Highly skilled specialist OT with expert knowledge in rheumatology OT, providing consultancy and developing others.",
    band: "Typically Band 7–8a",
    competencies: [
      { key: "expert_practice", label: "Expert-level clinical assessment, reasoning, and intervention in rheumatology OT" },
      { key: "service_development", label: "Leading service development and quality improvement initiatives" },
      { key: "education_delivery", label: "Providing education and training for junior OTs, students, and MDT colleagues" },
      { key: "research_application", label: "Critical appraisal and implementation of research evidence in practice" },
      { key: "supervision_provision", label: "Providing professional and clinical supervision to OT colleagues" },
    ],
    colour: "violet",
  },
  {
    key: "consultant",
    title: "Consultant",
    description: "Consultant OT with strategic influence, leading practice development across services and contributing to the profession nationally.",
    band: "Typically Band 8b–8c",
    competencies: [
      { key: "strategic_leadership", label: "Strategic clinical leadership across rheumatology OT services" },
      { key: "advanced_practice_scope", label: "Advanced scope of practice (e.g. prescribing, injection therapy, imaging referral)" },
      { key: "professional_advocacy", label: "Advocating for the occupational therapy profession in rheumatology at a strategic level" },
      { key: "research_generation", label: "Generating, disseminating, and implementing original research or service evaluations" },
      { key: "national_contribution", label: "Contributing to RCOT SS Rheumatology, BSR OT SIG, or national OT guidance development" },
    ],
    colour: "amber",
  },
  {
    key: "strategic",
    title: "Strategic / Fellow",
    description: "Strategic-level occupational therapist or RCOT Fellow — shaping national policy, education, and the future of the OT profession in rheumatology.",
    band: "RCOT Fellow (FRCOT) / Band 8d–9",
    competencies: [
      { key: "policy_influence", label: "Influencing national and international policy relevant to rheumatology OT" },
      { key: "frcot_pathway", label: "Meeting RCOT Fellowship (FRCOT) criteria in one or more categories" },
      { key: "national_leadership", label: "National or international leadership in occupational therapy or rheumatology" },
      { key: "workforce_transformation", label: "Transforming the OT workforce through education, research, or system-level change" },
      { key: "professional_legacy", label: "Creating a sustainable professional legacy through publications, training, or standards work" },
    ],
    colour: "rose",
  },
];

const COLOUR_MAP: Record<string, { header: string; icon: string; badge: string }> = {
  blue: { header: "from-blue-700 to-blue-900", icon: "text-blue-300", badge: "bg-blue-100" },
  violet: { header: "from-violet-700 to-violet-900", icon: "text-violet-300", badge: "bg-violet-100" },
  amber: { header: "from-amber-700 to-amber-900", icon: "text-amber-300", badge: "bg-amber-100" },
  rose: { header: "from-rose-700 to-rose-900", icon: "text-rose-300", badge: "bg-rose-100" },
};

interface PortfolioEntry {
  frameworkKey: string;
  stageKey: string;
  competencyKey: string;
  status: StageStatus;
  evidenceNotes: string | null;
}

export default function OtAdvancedPractice() {
  const [entries, setEntries] = useState<PortfolioEntry[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [localEdits, setLocalEdits] = useState<Record<string, { status: StageStatus; notes: string }>>({});

  const load = useCallback(async () => {
    const res = await fetch("/api/ot/portfolio");
    if (res.ok) {
      const data: PortfolioEntry[] = await res.json();
      const filtered = data.filter(e => e.frameworkKey === "rcot-advanced");
      setEntries(filtered);
      const edits: Record<string, { status: StageStatus; notes: string }> = {};
      for (const e of filtered) {
        edits[`${e.stageKey}:${e.competencyKey}`] = { status: e.status, notes: e.evidenceNotes ?? "" };
      }
      setLocalEdits(edits);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getEntry = (stageKey: string, competencyKey: string) => {
    const key = `${stageKey}:${competencyKey}`;
    return localEdits[key] ?? { status: "developing" as StageStatus, notes: "" };
  };

  const setStatus = (stageKey: string, competencyKey: string, status: StageStatus) => {
    const key = `${stageKey}:${competencyKey}`;
    setLocalEdits(prev => ({ ...prev, [key]: { ...prev[key] ?? { notes: "" }, status } }));
  };

  const setNotes = (stageKey: string, competencyKey: string, notes: string) => {
    const key = `${stageKey}:${competencyKey}`;
    setLocalEdits(prev => ({ ...prev, [key]: { ...prev[key] ?? { status: "developing" as StageStatus }, notes } }));
  };

  const saveEntry = async (stageKey: string, competencyKey: string) => {
    const key = `${stageKey}:${competencyKey}`;
    const edit = localEdits[key];
    if (!edit) return;
    setSaving(key);
    try {
      await fetch(`/api/ot/portfolio/rcot-advanced/${stageKey}/${competencyKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: edit.status, evidenceNotes: edit.notes || undefined }),
      });
    } finally {
      setSaving(null);
    }
  };

  const stageProgress = (stageKey: string, comps: { key: string }[]) => {
    const achieved = comps.filter(c => getEntry(stageKey, c.key).status === "achieved").length;
    return { achieved, total: comps.length, pct: Math.round((achieved / comps.length) * 100) };
  };

  return (
    <OTLayout title="RCOT Advanced Practice Portfolio">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-rose-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Star className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">RCOT Career Development Framework</h2>
              <p className="text-amber-200 text-sm">Four stages: Proficient → Expert → Consultant → Strategic/Fellow</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            Map your evidence against the RCOT Career Development Framework stages and competency areas. Track your progress toward Consultant OT or RCOT Fellowship. Evidence notes and status are saved to your personal portfolio.
          </p>
        </div>

        {RCOT_STAGES.map((stage) => {
          const progress = stageProgress(stage.key, stage.competencies);
          const c = COLOUR_MAP[stage.colour];
          const isOpen = expanded === stage.key;

          return (
            <div key={stage.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setExpanded(isOpen ? null : stage.key)}
                className={`w-full bg-gradient-to-r ${c.header} text-white p-5 flex items-start justify-between gap-4 text-left`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-bold">{stage.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.badge} text-slate-700`}>{stage.band}</span>
                  </div>
                  <p className={`text-sm ${c.icon}`}>{stage.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-2xl font-bold">{progress.achieved}/{progress.total}</p>
                  <p className="text-xs opacity-80">achieved</p>
                  {isOpen ? <ChevronUp className="h-4 w-4 mt-1 ml-auto" /> : <ChevronDown className="h-4 w-4 mt-1 ml-auto" />}
                </div>
              </button>

              {isOpen && (
                <div className="p-5 space-y-4">
                  <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
                    <div
                      className="bg-amber-500 h-2 rounded-full transition-all"
                      style={{ width: `${progress.pct}%` }}
                    />
                  </div>

                  {stage.competencies.map((comp) => {
                    const entry = getEntry(stage.key, comp.key);
                    const saveKey = `${stage.key}:${comp.key}`;
                    return (
                      <div key={comp.key} className="border border-slate-200 rounded-xl p-4 space-y-3">
                        <p className="text-sm font-semibold text-slate-800">{comp.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {(["developing", "achieving", "achieved"] as StageStatus[]).map((s) => (
                            <button
                              key={s}
                              onClick={() => setStatus(stage.key, comp.key, s)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                entry.status === s
                                  ? STATUS_COLORS[s] + " ring-2 ring-offset-1 ring-current"
                                  : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                              }`}
                            >
                              {STATUS_LABELS[s]}
                            </button>
                          ))}
                        </div>
                        <textarea
                          value={entry.notes}
                          onChange={(e) => setNotes(stage.key, comp.key, e.target.value)}
                          placeholder="Evidence notes — what have you done to demonstrate this competency?"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                        />
                        <div className="flex justify-end">
                          <button
                            onClick={() => saveEntry(stage.key, comp.key)}
                            disabled={saving === saveKey}
                            className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors font-medium"
                          >
                            {saving === saveKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            {saving === saveKey ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>RCOT Career Development Framework:</strong> Based on the Royal College of Occupational Therapists Career Development Framework (2021). Stage descriptors are adapted for rheumatology OT practice. Evidence of competency achievement should be validated through your trust appraisal process and professional supervision.
        </div>
      </div>
    </OTLayout>
  );
}
